/**
 * EPW File Fetcher
 *
 * Handles fetching EPW files from various sources, including extracting
 * from ZIP archives (as used by climate.onebuilding.org).
 *
 * External URLs from climate.onebuilding.org are routed through a proxy
 * to bypass CORS restrictions (the external site doesn't send CORS headers).
 *
 * Files are cached in IndexedDB for offline access and faster subsequent loads.
 */

import JSZip from 'jszip';
import { getCachedWeatherFile, cacheWeatherFile } from './weatherFileCache';

/**
 * Check if a URL needs to be proxied due to CORS restrictions.
 */
function needsProxy(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    // External URLs from climate.onebuilding.org need proxying
    return parsedUrl.hostname === 'climate.onebuilding.org';
  } catch {
    return false;
  }
}

/**
 * Get the proxied URL for external weather file downloads.
 */
function getProxiedUrl(url: string): string {
  // Use the Vercel API route to proxy the request
  return `/api/weather-proxy?url=${encodeURIComponent(url)}`;
}

/**
 * Generate a cache key from a URL.
 * Uses the URL as the cache key, normalized to avoid duplicates.
 */
function getCacheKey(url: string): string {
  // Use the full URL as the cache key
  return url;
}

/**
 * Fetch an EPW file from a URL.
 * If the URL points to a ZIP file, extracts the EPW file from within.
 * External URLs are routed through a proxy to bypass CORS restrictions.
 * Results are cached in IndexedDB for offline access and faster subsequent loads.
 *
 * @param url - URL to fetch (can be .epw or .zip)
 * @param skipCache - If true, bypass the cache and always fetch from network
 * @returns The EPW file content as a string
 */
export async function fetchEpwFile(url: string, skipCache = false): Promise<string> {
  const cacheKey = getCacheKey(url);

  // Check cache first (unless skipping)
  if (!skipCache) {
    try {
      const cached = await getCachedWeatherFile(cacheKey);
      if (cached) {
        console.log('[fetchEpw] Using cached weather file:', cacheKey);
        return cached.content;
      }
    } catch (err) {
      // Cache read failed, continue with network fetch
      console.warn('[fetchEpw] Cache read failed:', err);
    }
  }

  // Route external URLs through proxy to bypass CORS
  const fetchUrl = needsProxy(url) ? getProxiedUrl(url) : url;

  const response = await fetch(fetchUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch weather file: ${response.status} ${response.statusText}`);
  }

  let content: string;

  // Check if it's a ZIP file
  if (url.toLowerCase().endsWith('.zip')) {
    const arrayBuffer = await response.arrayBuffer();
    content = await extractEpwFromZip(arrayBuffer);
  } else {
    // Direct EPW file
    content = await response.text();
  }

  // Cache the result (async, fire-and-forget)
  cacheWeatherFile(cacheKey, content).catch((err) => {
    console.warn('[fetchEpw] Failed to cache weather file:', err);
  });

  return content;
}

/**
 * Extract the EPW file from a ZIP archive.
 *
 * @param zipData - The ZIP file as an ArrayBuffer
 * @returns The EPW file content as a string
 */
async function extractEpwFromZip(zipData: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(zipData);

  // Find the EPW file in the archive
  const epwFile = Object.keys(zip.files).find(
    (name) => name.toLowerCase().endsWith('.epw')
  );

  if (!epwFile) {
    throw new Error('No EPW file found in the ZIP archive');
  }

  const content = await zip.files[epwFile]?.async('string');

  if (!content) {
    throw new Error('Failed to extract EPW file from ZIP archive');
  }

  return content;
}
